<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Fiken konto</h3>
        <div v-if='updated'>
          <span class='message-info'>Fiken kontoinnstillingene er oppdatert</span>
        </div>

        <div v-if="locations.length === 0 && !loading" class="mt-4">
          <span class="message-info">Ingen avdelinger har Fiken aktivert. Aktiver det under Avdelinger.</span>
        </div>

        <div v-if="locations.length > 0 && hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">For avdeling</span>
          <select class="form-select" v-model="locationId" @change="getFikenAccount()">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <template v-if="locations.length > 0">
          <!-- Fiken has no test environment in this integration — there is only the live API — so the
               lock closes as soon as a location is connected at all, rather than on an environment
               switch the way Unimicro and Tripletex do. See AccountingEnv. -->
          <config-lock ref="lock" :configured="isProduction">
          <div class="mt-4">
            <span class='ingredient-title'>Client ID</span>
            <input class='form-control' type='text' v-model='fikenAccount.clientId' />
          </div>

          <div>
            <span class='ingredient-title'>Client Secret</span>
            <input class='form-control' type='text' v-model='fikenAccount.clientSecret' />
          </div>

          <div class="mt-2" style="font-size:0.85em; color:var(--bo-muted);">
            For å få Client ID og Client Secret: Logg inn på Fiken → Rediger konto → Profil → Andre innstillinger → huk av at du er utvikler → klikk «API»-fanen under Brukerinnstillinger for å opprette en app.
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Tilkoblingsstatus</span>
            <span v-if="fikenAccount.accessToken" class="ms-2 text-success fw-bold">Tilkoblet</span>
            <span v-else class="ms-2 text-danger fw-bold">Ikke tilkoblet</span>
          </div>

          <div v-if="fikenAccount.companyName" class="mt-2">
            <span class='ingredient-title'>Valgt firma</span>
            <span class="ms-2">{{ fikenAccount.companyName }} ({{ fikenAccount.companySlug }})</span>
          </div>

          <div class='bo_hr mt-4'>
            <hr/>
          </div>

          <div class='d-flex gap-3 mb-3'>
            <button v-if='activateSaveButton' type='button' class='bo_btn' @click='save'> Lagre </button>
            <button v-else type='button' class='bo_btn' @click='save' disabled='true'> Lagre </button>

            <button v-if='activateConnectButton' type='button' class='bo_btn' style="background-color:#28a745" @click='connectToFiken'> Koble til Fiken </button>
            <button v-else type='button' class='bo_btn' style="background-color:var(--bo-muted)" disabled='true'> Koble til Fiken </button>

          </div>
          </config-lock>
          <div v-if="connectionTested" class="mb-5">
            <span :class="connectionSuccess ? 'message-success' : 'message-error'">{{ connectionMessage }}</span>
          </div>
        </template>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import MerchantUtil from "@/util/MerchantUtil";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import ConfigLock from "@/components/other/ConfigLock.vue";
  import AccountingEnv from "@/util/AccountingEnv";

  export default {
    components: {LoadingSpinner, ConfigLock},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],

        updated: false,
        connectionTested: false,
        connectionSuccess: false,
        connectionMessage: '',

        fikenAccount: {
          type: "fiken_account",
          merchant: '',
          locationId: '',
          clientId: '',
          clientSecret: '',
          accessToken: '',
          companySlug: '',
          companyName: ''
        }
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'FIKEN');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.getFikenAccount();
            } else {
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    computed: {
      // No environment to read: Fiken is live or it is nothing. Connected == production.
      isProduction(){
        return AccountingEnv.fikenIsProduction(this.fikenAccount);
      },
      activateSaveButton(){
        if(this.fikenAccount.clientId.length > 0 &&
            this.fikenAccount.clientSecret.length > 0){
          return true;
        } else {
          return false;
        }
      },
      activateConnectButton(){
        if(this.fikenAccount.id &&
            this.fikenAccount.clientId.length > 0 &&
            this.fikenAccount.clientSecret.length > 0){
          return true;
        } else {
          return false;
        }
      }
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      getFikenAccount(){
        SettingsService.getFikenAccount(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.fikenAccount = response.data;
              }

              this.loading = false;
              // Each location is its own connection: an unlock for one must not carry to the next.
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());

              if(this.fikenAccount.accessToken){
                this.testConnection();
              }
            },
            (error) => {
              console.log(error);
            }
        );
      },
      save(){
        this.loading = true;
        this.fikenAccount.merchant = MerchantUtil.getMerchant();
        this.fikenAccount.locationId = this.locationId;

        SettingsService.editFikenAccount(this.fikenAccount).then(
            (response) => {
              let documentId = response.data;
              this.fikenAccount.id = documentId;
              if(documentId){
                this.updated = true;
              } else {
                console.log("Editing fikenAccount failed");
              }
              this.loading = false;
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
            }
        );
      },
      testConnection(){
        this.loading = true;
        this.connectionTested = false;
        SettingsService.testFikenConnection(this.locationId).then(
            (response) => {
              this.connectionTested = true;
              this.connectionSuccess = response.data.success;
              this.connectionMessage = response.data.message;
              this.loading = false;
            },
            (error) => {
              this.connectionTested = true;
              this.connectionSuccess = false;
              this.connectionMessage = 'Feil ved tilkobling';
              console.log(error);
              this.loading = false;
            }
        );
      },
      connectToFiken(){
        this.loading = true;
        SettingsService.getFikenAuthorizationUrl(this.locationId).then(
            (response) => {
              if(response.data && response.data.authorizationUrl){
                window.location.href = response.data.authorizationUrl;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      }
    }
  };
</script>
